/**
 * Custom hook for managing PDF page navigation
 *
 * Extracted from PDFViewerModal.tsx
 */

import { useState, useEffect, useCallback } from 'react';

export interface UsePDFNavigationProps {
  initialPage?: number;
  numPages: number;
  onPageChange?: (page: number) => void;
}

export interface UsePDFNavigationReturn {
  pageNumber: number;
  setPageNumber: (page: number | ((prev: number) => number)) => void;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
}

/**
 * Manages PDF page navigation state and handlers
 */
export function usePDFNavigation({
  initialPage,
  numPages,
  onPageChange,
}: UsePDFNavigationProps): UsePDFNavigationReturn {
  const [pageNumber, setPageNumberState] = useState<number>(initialPage || 1);

  // Sync with external page prop
  useEffect(() => {
    if (initialPage && initialPage !== pageNumber) {
      setPageNumberState(initialPage);
    }
  }, [initialPage]);

  const setPageNumber = useCallback((page: number | ((prev: number) => number)) => {
    setPageNumberState(prev => {
      const newPage = typeof page === 'function' ? page(prev) : page;
      // Call onPageChange after render
      setTimeout(() => {
        onPageChange?.(newPage);
      }, 0);
      return newPage;
    });
  }, [onPageChange]);

  const goToPreviousPage = useCallback(() => {
    setPageNumber(prev => Math.max(1, prev - 1));
  }, [setPageNumber]);

  const goToNextPage = useCallback(() => {
    setPageNumber(prev => Math.min(numPages, prev + 1));
  }, [setPageNumber, numPages]);

  return {
    pageNumber,
    setPageNumber,
    goToPreviousPage,
    goToNextPage,
  };
}
